import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage, PRIMARY_LANGUAGES, SECONDARY_LANGUAGES, LANGUAGES } from '../../context/LanguageContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';

export function Navbar({ onBriefClick }) {
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isMobileLangMenuOpen, setIsMobileLangMenuOpen] = useState(false);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const langMenuRef = useRef(null);
  const mobileLangMenuRef = useRef(null);
  const companyMenuRef = useRef(null);
  
  const handleCTAClick = () => {
    const currentLang = location.pathname.split('/')[1];
    const lang = LANGUAGES.some(l => l.code === currentLang) ? currentLang : 'en';
    navigate(`/${lang}/checkout?plan=auction`);
  };

  // Handle scroll for sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
      if (mobileLangMenuRef.current && !mobileLangMenuRef.current.contains(event.target)) {
        setIsMobileLangMenuOpen(false);
      }
      if (companyMenuRef.current && !companyMenuRef.current.contains(event.target)) {
        setIsCompanyMenuOpen(false);
      }
    };

    if (isLangMenuOpen || isMobileLangMenuOpen || isCompanyMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isLangMenuOpen, isMobileLangMenuOpen, isCompanyMenuOpen]);

  // Main navigation links (desktop)
  const mainNavLinks = [
    { href: '#offer', label: t('nav.offer') },
    { href: '#solutions', label: t('nav.solutions') },
    { href: '#cases', label: t('nav.cases') },
    { href: '#process', label: t('nav.process') },
    { href: '#pricing', label: t('nav.pricing') }
  ];

  // Company dropdown links
  const companyLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#team', label: t('nav.team') },
    { href: '#approach', label: t('nav.approach') },
    { href: '#comparison', label: t('nav.comparison') },
    { href: '#faq', label: t('nav.faq') },
    { href: '#contact', label: t('nav.contact') }
  ];

  // All links for mobile menu
  const allLinks = [...mainNavLinks, ...companyLinks];

  const currentLang = PRIMARY_LANGUAGES.find(l => l.code === language) || 
                      SECONDARY_LANGUAGES.find(l => l.code === language);

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        "backdrop-blur-xl bg-slate-950/80 border-b border-white/5",
        isScrolled ? "py-3 shadow-lg shadow-black/20" : "py-5"
      )}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-8">
        {/* Mobile Menu Button - LEFT */}
        <button 
          className="lg:hidden p-2 text-white hover:text-primary transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          data-testid="mobile-menu-btn"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Logo - LEFT */}
        <a 
          href={`/${language}/`} 
          className={cn(
            "font-heading font-bold text-white transition-all duration-300",
            isScrolled ? "text-xl" : "text-2xl"
          )}
          data-testid="logo"
        >
          Dathent
        </a>

        {/* Desktop Navigation - CENTER */}
        <div className="hidden lg:flex items-center gap-8 flex-1 justify-center">
          {mainNavLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="group relative text-slate-300 hover:text-white transition-colors font-medium whitespace-nowrap"
              data-testid={`nav-${link.href.replace('#', '')}`}
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-purple-400 group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          
          {/* Company Dropdown */}
          <div className="relative" ref={companyMenuRef}>
            <button
              onClick={() => setIsCompanyMenuOpen(!isCompanyMenuOpen)}
              className="group relative flex items-center gap-1 text-slate-300 hover:text-white transition-colors font-medium whitespace-nowrap"
              data-testid="nav-company"
            >
              {t('nav.company')}
              <ChevronDown 
                size={16} 
                className={cn(
                  "transition-transform duration-200",
                  isCompanyMenuOpen && "rotate-180"
                )}
              />
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-purple-400 group-hover:w-full transition-all duration-300" />
            </button>

            {isCompanyMenuOpen && (
              <div className="absolute top-full left-0 mt-3 py-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/40 min-w-[200px] animate-in fade-in slide-in-from-top-2 duration-200">
                {companyLinks.map((link, index) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsCompanyMenuOpen(false)}
                    className={cn(
                      "block px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors",
                      index === 3 && "border-t border-white/5 mt-1 pt-3"
                    )}
                    data-testid={`company-${link.href.replace('#', '')}`}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Language + CTA */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language Switcher - Desktop */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-200 text-white border border-white/5"
              data-testid="lang-switcher-btn"
            >
              <Globe size={16} />
              <span className="font-mono text-sm font-medium">{currentLang?.label}</span>
              <ChevronDown 
                size={14} 
                className={cn(
                  "transition-transform duration-200",
                  isLangMenuOpen && "rotate-180"
                )}
              />
            </button>

            {isLangMenuOpen && (
              <div className="absolute top-full right-0 mt-2 py-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl shadow-black/40 min-w-[180px] animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Primary Languages */}
                {PRIMARY_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={cn(
                      'w-full px-4 py-2 text-left hover:bg-white/5 transition-all duration-150 flex items-center gap-2.5 group',
                      language === lang.code && 'bg-white/5'
                    )}
                    data-testid={`lang-${lang.code}`}
                  >
                    {language === lang.code && (
                      <span className="w-0.5 h-4 bg-gradient-to-b from-primary to-purple-400 rounded-full" />
                    )}
                    <span className={cn(
                      "text-sm transition-colors",
                      language === lang.code ? "text-white font-medium" : "text-slate-300 group-hover:text-white"
                    )}>
                      {lang.name}
                    </span>
                  </button>
                ))}
                
                {/* Divider */}
                <div className="h-px bg-white/5 my-2 mx-3" />
                
                {/* Secondary Languages */}
                {SECONDARY_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    className={cn(
                      'w-full px-4 py-2 text-left hover:bg-white/5 transition-all duration-150 flex items-center gap-2.5 group',
                      language === lang.code && 'bg-white/5'
                    )}
                    data-testid={`lang-${lang.code}`}
                  >
                    {language === lang.code && (
                      <span className="w-0.5 h-4 bg-gradient-to-b from-primary to-purple-400 rounded-full" />
                    )}
                    <span className={cn(
                      "text-sm transition-colors",
                      language === lang.code ? "text-white font-medium" : "text-slate-300 group-hover:text-white"
                    )}>
                      {lang.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleCTAClick}
            className={cn(
              "relative overflow-hidden bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90",
              "shadow-lg shadow-primary/25 hover:shadow-primary/40",
              "transition-all duration-300",
              isScrolled ? "px-4 py-2 text-sm" : "px-5 py-2.5"
            )}
            data-testid="header-cta"
          >
            <span className="relative z-10">{t('nav.cta')}</span>
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          </Button>
        </div>

        {/* Mobile Language Button - RIGHT */}
        <div className="lg:hidden relative" ref={mobileLangMenuRef}>
          <button
            onClick={() => setIsMobileLangMenuOpen(!isMobileLangMenuOpen)}
            className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-white/5 text-white border border-white/5"
            data-testid="mobile-lang-btn"
          >
            <Globe size={16} />
            <span className="font-mono text-sm">{currentLang?.label}</span>
          </button>

          {isMobileLangMenuOpen && (
            <div className="absolute top-full right-0 mt-2 py-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl min-w-[180px]">
              {/* Primary Languages */}
              {PRIMARY_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsMobileLangMenuOpen(false);
                  }}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-white/5 transition-all flex items-center gap-2.5',
                    language === lang.code && 'bg-white/5'
                  )}
                  data-testid={`mobile-lang-${lang.code}`}
                >
                  {language === lang.code && (
                    <span className="w-0.5 h-4 bg-gradient-to-b from-primary to-purple-400 rounded-full" />
                  )}
                  <span className={cn(
                    "text-sm",
                    language === lang.code ? "text-white font-medium" : "text-slate-300"
                  )}>
                    {lang.name}
                  </span>
                </button>
              ))}
              
              {/* Divider */}
              <div className="h-px bg-white/5 my-2 mx-3" />
              
              {/* Secondary Languages */}
              {SECONDARY_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsMobileLangMenuOpen(false);
                  }}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-white/5 transition-all flex items-center gap-2.5',
                    language === lang.code && 'bg-white/5'
                  )}
                  data-testid={`mobile-lang-${lang.code}`}
                >
                  {language === lang.code && (
                    <span className="w-0.5 h-4 bg-gradient-to-b from-primary to-purple-400 rounded-full" />
                  )}
                  <span className={cn(
                    "text-sm",
                    language === lang.code ? "text-white font-medium" : "text-slate-300"
                  )}>
                    {lang.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-slate-900/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-4 flex flex-col gap-3">
            {allLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  "text-slate-300 hover:text-white transition-colors font-medium py-2",
                  index === mainNavLinks.length && "border-t border-white/5 pt-4 mt-1"
                )}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            
            {/* Mobile CTA */}
            <Button
              onClick={() => {
                onBriefClick();
                setIsMenuOpen(false);
              }}
              className="mt-4 w-full bg-gradient-to-r from-primary to-purple-500 shadow-lg shadow-primary/25"
            >
              {t('nav.cta')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
